// src/services/apiServices.js
import api from '../api/apiClient';

/**
 * Authentication services
 */
export const authService = {
  // Login user
  login: async (email, password) => {
    try {
      return await api.post('/auth/login', { email, password });
    } catch (error) {
      console.error('Login API error:', error);
      throw error;
    }
  },

  // Refresh token - Token is sent automatically via HttpOnly cookie
  refreshToken: async () => {
    try {
      // No need to pass refreshToken - it's automatically sent via HttpOnly cookie
      return await api.post('/auth/refresh', {}, {
        withCredentials: true // Ensure cookies are sent
      });
    } catch (error) {
      console.error('Refresh token API error:', error);
      throw error;
    }
  },

  // Logout user
  logout: async () => {
    try {
      return await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout API error:', error);
      // Even if the API call fails, we still want to clear local storage
      return { success: true };
    }
  },

  // DEPRECATED: Heartbeat system removed - using pure JWT authentication
  // This function is kept for backwards compatibility and does nothing
  heartbeat: async () => {
    // No-op - heartbeat system has been removed
    return { success: true, message: 'Heartbeat deprecated' };
  },

  // Get current user info
  getCurrentUser: async (silentAuth = false) => {
    try {
      // Pass silentAuth option to prevent page reload on token expiry
      return await api.get('/auth/me', {}, { silentAuth });
    } catch (error) {
      console.error('Get current user API error:', error);
      throw error;
    }
  },

  // MFA Setup - Get QR code and secret
  mfaSetup: async () => {
    try {
      return await api.get('/auth/mfa/setup');
    } catch (error) {
      console.error('MFA setup API error:', error);
      throw error;
    }
  },

  // MFA Verify - Activate MFA after scanning QR code
  mfaVerify: async (totpCode) => {
    try {
      return await api.post('/auth/mfa/verify', { totpCode });
    } catch (error) {
      console.error('MFA verify API error:', error);
      throw error;
    }
  },

  // MFA Disable - Deactivate MFA
  mfaDisable: async (password) => {
    try {
      return await api.post('/auth/mfa/disable', { password });
    } catch (error) {
      console.error('MFA disable API error:', error);
      throw error;
    }
  },

  // MFA Trust Device - Mark current device as trusted for 30 days
  mfaTrustDevice: async () => {
    try {
      return await api.post('/auth/mfa/trust-device');
    } catch (error) {
      console.error('MFA trust device API error:', error);
      throw error;
    }
  },

  // MFA Confirm Login - Second step after TOTP code
  mfaConfirmLogin: async (tempToken, totpCode, trustDevice = false) => {
    try {
      return await api.post('/auth/mfa/confirm', { tempToken, totpCode, trustDevice });
    } catch (error) {
      console.error('MFA confirm login API error:', error);
      throw error;
    }
  },

  // MFA Status - Get current user MFA status
  mfaStatus: async () => {
    try {
      return await api.get('/auth/mfa/status');
    } catch (error) {
      console.error('MFA status API error:', error);
      throw error;
    }
  }
};

/**
 * User management services
 */
export const userService = {
  // Get all users (with filters)
  getUsers: async (filters = {}) => {
    try {
      return await api.get('/users', filters);
    } catch (error) {
      console.error('Get users API error:', error);
      throw error;
    }
  },

  // Get user by ID
  getUserById: async (id) => {
    try {
      return await api.get(`/users/${id}`);
    } catch (error) {
      console.error('Get user API error:', error);
      throw error;
    }
  },

  // Create new user
  createUser: async (userData) => {
    try {
      return await api.post('/users', userData);
    } catch (error) {
      console.error('Create user API error:', error);
      throw error;
    }
  },

  // Update user
  updateUser: async (id, userData) => {
    try {
      return await api.put(`/users/${id}`, userData);
    } catch (error) {
      console.error('Update user API error:', error);
      throw error;
    }
  },

  // Update user password
  updatePassword: async (id, { currentPassword, newPassword }) => {
    try {
      return await api.put(`/users/${id}/password`, { currentPassword, newPassword });
    } catch (error) {
      console.error('Update password API error:', error);
      throw error;
    }
  },

  // Force logout a user's active session
  forceLogout: async (id) => {
    try {
      return await api.post(`/users/${id}/force-logout`);
    } catch (error) {
      console.error('Force logout API error:', error);
      throw error;
    }
  },

  // Accept CGU
  acceptCgu: async () => {
    try {
      return await api.put('/users/accept-cgu');
    } catch (error) {
      console.error('Accept CGU API error:', error);
      throw error;
    }
  },

  // Delete user
  deleteUser: async (id) => {
    try {
      return await api.delete(`/users/${id}`);
    } catch (error) {
      console.error('Delete user API error:', error);
      throw error;
    }
  },

  // Get user logs
  getUserLogs: async (userId, filters = {}) => {
    try {
      return await api.get(`/users/${userId}/logs`, filters);
    } catch (error) {
      console.error('Get user logs API error:', error);
      throw error;
    }
  },

  // Update user permissions
  updateUserPermissions: async (userId, permissions) => {
    try {
      return await api.put(`/users/${userId}/permissions`, permissions);
    } catch (error) {
      console.error('Update user permissions API error:', error);
      throw error;
    }
  },

  // Update user creation limits (SuperAdmin only)
  updateUserCreationLimits: async (userId, limits) => {
    try {
      return await api.put(`/users/${userId}/limits`, limits);
    } catch (error) {
      console.error('Update user creation limits API error:', error);
      throw error;
    }
  },

  // Admin - Toggle MFA required for a user
  toggleMfaRequired: async (userId, required) => {
    try {
      return await api.put(`/users/${userId}/mfa-require`, { required });
    } catch (error) {
      console.error('Toggle MFA required API error:', error);
      throw error;
    }
  },

  // Admin - Get MFA status for a user
  getMfaStatus: async (userId) => {
    try {
      return await api.get(`/users/${userId}/mfa-status`);
    } catch (error) {
      console.error('Get MFA status API error:', error);
      throw error;
    }
  },

  // Admin - Reset MFA for a user (lost access recovery)
  resetMfa: async (userId) => {
    try {
      return await api.post(`/users/${userId}/mfa-reset`);
    } catch (error) {
      console.error('Reset MFA API error:', error);
      throw error;
    }
  }
};

/**
 * Company management services
 */
export const companyService = {
  // Get all companies (with filters)
  getCompanies: async (filters = {}) => {
    try {
      return await api.get('/companies', filters);
    } catch (error) {
      console.error('Get companies API error:', error);
      throw error;
    }
  },

  // Get company by ID
  getCompanyById: async (id) => {
    try {
      return await api.get(`/companies/${id}`);
    } catch (error) {
      console.error('Get company API error:', error);
      throw error;
    }
  },

  // Create new company
  createCompany: async (companyData) => {
    try {
      return await api.post('/companies', companyData);
    } catch (error) {
      console.error('Create company API error:', error);
      throw error;
    }
  },

  // Update company
  updateCompany: async (id, companyData) => {
    try {
      return await api.put(`/companies/${id}`, companyData);
    } catch (error) {
      console.error('Update company API error:', error);
      throw error;
    }
  },

  // Delete company
  deleteCompany: async (id) => {
    try {
      return await api.delete(`/companies/${id}`);
    } catch (error) {
      console.error('Delete company API error:', error);
      throw error;
    }
  },

  // Get company users
  getCompanyUsers: async (companyId) => {
    try {
      return await api.get(`/companies/${companyId}/users`);
    } catch (error) {
      console.error('Get company users API error:', error);
      throw error;
    }
  },

  // Get company logs
  getCompanyLogs: async (companyId, filters = {}) => {
    try {
      return await api.get(`/companies/${companyId}/logs`, filters);
    } catch (error) {
      console.error('Get company logs API error:', error);
      throw error;
    }
  },

  // Get company metrics
  getCompanyMetrics: async () => {
    try {
      return await api.get('/companies/metrics');
    } catch (error) {
      console.error('Get company metrics API error:', error);
      throw error;
    }
  },

  // Update company subscription status
  updateCompanySubscription: async (companyId, subscriptionData) => {
    try {
      return await api.put(`/companies/${companyId}/subscription`, subscriptionData);
    } catch (error) {
      console.error('Update company subscription API error:', error);
      throw error;
    }
  },

  // Get company subscription
  getCompanySubscription: async (companyId) => {
    try {
      return await api.get(`/companies/${companyId}/subscription`);
    } catch (error) {
      console.error('Get company subscription API error:', error);
      throw error;
    }
  }
};

/**
 * Activity logs services
 */
export const logService = {
  // Get all activity logs (with filters)
  getLogs: async (filters = {}) => {
    try {
      const response = await api.get('/logs', filters);
      return response;
    } catch (error) {
      console.error('Get logs API error:', error);
      throw error;
    }
  },

  // Get activity stats
  getActivityStats: async () => {
    try {
      return await api.get('/logs/stats');
    } catch (error) {
      console.error('Get activity stats API error:', error);
      throw error;
    }
  }
};

/**
 * Subscription Request services
 */
export const subscriptionRequestService = {
  // Create subscription request
  createRequest: async (requestData) => {
    try {
      return await api.post('/subscription-requests', requestData);
    } catch (error) {
      console.error('Create subscription request API error:', error);
      throw error;
    }
  },

  // Get all subscription requests (SuperAdmin)
  getAllRequests: async (filters = {}) => {
    try {
      return await api.get('/subscription-requests', filters);
    } catch (error) {
      console.error('Get all subscription requests API error:', error);
      throw error;
    }
  },

  // Get current company's requests
  getMyCompanyRequests: async () => {
    try {
      return await api.get('/subscription-requests/my-company');
    } catch (error) {
      console.error('Get my company requests API error:', error);
      throw error;
    }
  },

  // Get subscription request by ID
  getRequestById: async (id) => {
    try {
      return await api.get(`/subscription-requests/${id}`);
    } catch (error) {
      console.error('Get subscription request by ID API error:', error);
      throw error;
    }
  },

  // Approve subscription request
  approveRequest: async (id, data) => {
    try {
      return await api.put(`/subscription-requests/${id}/approve`, data);
    } catch (error) {
      console.error('Approve subscription request API error:', error);
      throw error;
    }
  },

  // Reject subscription request
  rejectRequest: async (id, data) => {
    try {
      return await api.put(`/subscription-requests/${id}/reject`, data);
    } catch (error) {
      console.error('Reject subscription request API error:', error);
      throw error;
    }
  },

  // Get subscription request statistics
  getStatistics: async () => {
    try {
      return await api.get('/subscription-requests/statistics');
    } catch (error) {
      console.error('Get subscription request statistics API error:', error);
      throw error;
    }
  },

  // Delete subscription request
  deleteRequest: async (id) => {
    try {
      return await api.delete(`/subscription-requests/${id}`);
    } catch (error) {
      console.error('Delete subscription request API error:', error);
      throw error;
    }
  },

  // Reply to subscription request (approve or reject)
  replyToRequest: async (id, replyData) => {
    try {
      const { status, adminResponse, executionNotes, executed } = replyData;

      if (status === 'approved') {
        return await api.put(`/subscription-requests/${id}/approve`, {
          adminResponse,
          executionNotes,
          executed
        });
      } else if (status === 'rejected') {
        return await api.put(`/subscription-requests/${id}/reject`, {
          adminResponse
        });
      } else {
        throw new Error('Invalid status. Must be "approved" or "rejected"');
      }
    } catch (error) {
      console.error('Reply to subscription request API error:', error);
      throw error;
    }
  }
};

// Dashboard services removed (Use src/services/dashboardServices.js)


// Security Control services removed (Use src/services/securityControlsService.js)

